import mqtt from 'precompiled-mqtt';
import { isScheduleLive } from '../../class-utilities';
import { useParams } from 'react-router';
import { Skeleton } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { memo, useMemo } from 'react';
import { goApi } from '../../services';
import socketService from '../../useSocketService';
const PAGE_SIZE = 50;
interface FormattedDateTimeFieldProps {
    record?: Record<string, any>;
    source?: string;
}

const flattenObject = (obj: Record<string, any>) => {
    const flat: Record<string, any> = {};
    Object.entries(obj).forEach(([key, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([subKey, subValue]) => {
                flat[`${key}.${subKey}`] = subValue;
            });
        } else {
            flat[key] = value;
        }
    });
    return flat;
};

export function formatDistanceToNow(date: Date | string) {
    const now = new Date();
    const target = typeof date === 'string' ? new Date(date) : date;
    const diffMs = now.getTime() - target.getTime();

    const sec = Math.floor(diffMs / 1000);
    const min = Math.floor(sec / 60);
    const hour = Math.floor(min / 60);
    const day = Math.floor(hour / 24);
    const week = Math.floor(day / 7);
    const month = Math.floor(day / 30);
    const year = Math.floor(day / 365);

    if (sec < 60) return `${sec}sec ago`;
    if (min < 60) return `${min}min ago`;
    if (hour < 24) return `${hour}hr ago`;
    if (day < 7) return `${day}day ago`;
    if (week < 5) return `${week}weeks ago`;
    if (month < 12) return `${month}months ago`;
    return `${year}y ago`;
}

const FormattedDateTimeField = ({ record, source = 'createdAt' }: FormattedDateTimeFieldProps) => {
    const flatRecord = useMemo(() => (record ? flattenObject(record) : {}), [record]);
    if (!record) return null;
    const formatRelative = (dateString: string) => {
        if (!dateString) return null;
        return formatDistanceToNow(dateString);
    };
    if (source === 'createdAt') {
        return (
            <div className="flex flex-col text-center text-sm space-y-1">
                {record.createdAt && (
                    <div className="flex items-center space-x-2">
                        <div className="whitespace-nowrap text-[13px]">
                            {formatRelative(record.createdAt)}
                        </div>
                    </div>
                )}
            </div>
        );
    }
    const value = flatRecord[source];
    if (!value) return null;
    return <div className="text-xs text-gray-600">{formatRelative(value)}</div>;
};

export async function sendChat(message: string, scheduleId: string) {
    try {
        try {
            const result = await goApi.post<any>(`/batches/schedules/${scheduleId}/chats`, {
                message,
            });

            return result;
        } catch (err) {
            throw err;
        }
    } catch (e) {
        console.error('sendChat error:', e);
        return null;
    }
}

const COLORS = [
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-orange-500',
];

const stringToColorIndex = (str: string, colorsLength: number) => {
    str = str.trim().toLowerCase();
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = (hash * 33) ^ str.charCodeAt(i);
    }
    return Math.abs(hash) % colorsLength;
};

const ChatMessage = memo(({ record }: { record: any }) => {
    const nameParts = record.userName?.trim().split(' ') || [];
    const firstInitial = nameParts[0]?.[0]?.toUpperCase() || '';
    const lastInitial = nameParts[1]?.[0]?.toUpperCase() || '';
    const initials = lastInitial ? `${firstInitial}${lastInitial}` : firstInitial;
    const bgColor = COLORS[stringToColorIndex(record.userName || '', COLORS.length)];

    return (
        <div
            key={record.id || `${record.message}_${record.createdAt}_${record.userName}`}
            className="flex flex-row gap-2 items-start"
        >
            <div
                className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-bold ${bgColor}`}
            >
                {initials}
            </div>
            <div className="flex flex-col">
                <div className="text-md font-bold">{record.userName}</div>
                <div className="break-words pr-2">{record.message}</div>
                <div className="text-xs text-gray-500">
                    <FormattedDateTimeField record={record} source="createdAt" />
                </div>
            </div>
        </div>
    );
});

export const AllChats = ({
    chatType,
    scheduleId,
    liveChats,
    isLive,
}: {
    chatType: string;
    scheduleId: string;
    liveChats: any;
    isLive: boolean;
}) => {
    const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [replyText, setReplyText] = useState('');
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const [localLiveChats, setLocalLiveChats] = useState<any[]>(liveChats ?? []);
    const [messages, setMessages] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const fetchChats = useCallback(
        async (page: number) => {
            try {
                if (page === 1) {
                    setIsLoading(true);
                } else {
                    setIsFetchingNextPage(true);
                }

                const queryParams = new URLSearchParams({
                    page: page.toString(),
                    limit: PAGE_SIZE.toString(),
                });

                if (debouncedSearch) {
                    queryParams.append('name', debouncedSearch);
                }

                const res = await goApi.get(
                    `/batches/schedules/${scheduleId}/chats?${queryParams.toString()}`,
                );
                const newData = res.data?.items || [];

                if (page === 1) {
                    setMessages(newData);
                } else {
                    setMessages(prev => [...prev, ...newData]);
                }

                setHasNextPage(newData.length >= PAGE_SIZE);
            } catch (error) {
                console.error('Error fetching chats:', error);
            } finally {
                setIsLoading(false);
                setIsFetchingNextPage(false);
            }
        },
        [scheduleId, debouncedSearch],
    );

    const fetchNextPage = useCallback(() => {
        if (!isFetchingNextPage && hasNextPage) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchChats(nextPage);
        }
    }, [isFetchingNextPage, hasNextPage, currentPage, fetchChats]);

    useEffect(() => {
        setCurrentPage(1);
        setMessages([]);
        setHasNextPage(true);
        fetchChats(1);
    }, [scheduleId, debouncedSearch, fetchChats]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    fetchNextPage();
                }
            },
            { threshold: 1.0 },
        );
        if (loadMoreRef.current) observer.observe(loadMoreRef.current);
        return () => {
            if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
        };
    }, [fetchNextPage]);

    useEffect(() => {
        if (!liveChats?.length) return;

        setLocalLiveChats(prev => {
            const getKey = (msg: any) =>
                msg.id
                    ? `id:${msg.id}`
                    : `m:${(msg?.message || '').substring(0, 50)}_${msg?.userName}_${new Date(
                          msg?.createdAt,
                      )
                          .toISOString()
                          .substring(0, 16)}`;

            const merged = [...liveChats, ...prev];
            const seen = new Set<string>();
            const unique: any[] = [];

            for (const m of merged) {
                const k = getKey(m);
                if (!seen.has(k)) {
                    seen.add(k);
                    unique.push(m);
                }
            }
            return unique;
        });
    }, [liveChats]);

    const combinedMessages = useMemo(() => {
        const all = [...localLiveChats, ...messages];
        const seen = new Set();
        const uniqueMessages = [];

        for (const msg of all) {
            const msgId =
                msg.id ||
                `${msg.message.substring(0, 50)}_${msg.userName}_${new Date(msg.createdAt)
                    .toISOString()
                    .substring(0, 16)}`;
            if (!seen.has(msgId)) {
                seen.add(msgId);
                uniqueMessages.push(msg);
            }
        }

        return uniqueMessages.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    }, [localLiveChats, messages]);

    const handleReply = useCallback(async () => {
        if (!replyText.trim()) {
            return;
        }
        const messageText = replyText.trim();

        const userName =
            user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`.trim()
                : user?.name || 'You';

        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const temp = {
            id: tempId,
            message: messageText,
            userName,
            createdAt: new Date().toISOString(),
            __temp: true,
        } as any;

        setReplyText('');
        if (chatType !== 'SOCKET') {
            setLocalLiveChats(prev => [temp, ...prev]);
        }

        try {
            const resp = await sendChat(messageText, scheduleId);

            if (chatType !== 'SOCKET') {
                const created = resp?.data ?? resp;
                if (created?.id) {
                    setLocalLiveChats(prev => {
                        const filtered = prev.filter(p => p.id !== tempId);
                        return [created, ...filtered];
                    });
                }
            }
        } catch (apiError) {
            console.error('API send failed:', apiError);
            setLocalLiveChats(prev => prev.filter(p => p.id !== tempId));
            setReplyText(messageText);
        }
    }, [replyText, user, chatType, scheduleId]);

    return (
        <div className="flex-1 h-full flex flex-col relative">
            <div className="sticky top-0 bg-white z-10 p-2 border-b">
                <input
                    className="border rounded w-full p-2"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search Student"
                />
            </div>

            <div className="flex-1 overflow-y-auto px-10 pb-20">
                {isLoading ? (
                    <div className="p-4">Loading...</div>
                ) : combinedMessages?.length === 0 ? (
                    <div className="p-4">No chats found.</div>
                ) : (
                    <div className="flex flex-col gap-3 py-5">
                        {combinedMessages?.map((record: any) => (
                            <ChatMessage
                                key={
                                    record.id ||
                                    `${record.message}_${record.createdAt}_${record.userName}`
                                }
                                record={record}
                            />
                        ))}
                    </div>
                )}

                {combinedMessages?.length > 0 && (
                    <div ref={loadMoreRef} className="text-center py-4 text-sm text-gray-500">
                        {isFetchingNextPage
                            ? 'Loading more...'
                            : hasNextPage
                            ? 'Scroll to load more'
                            : 'No more chats'}
                    </div>
                )}
            </div>
            {isLive && (
                <div className="sticky bottom-2 bg-white border-t p-2 flex gap-2 items-center shadow-lg">
                    <input
                        type="text"
                        className="flex-1 border rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') handleReply();
                        }}
                        placeholder="Type your reply..."
                    />
                    <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm sm:text-base whitespace-nowrap"
                        onClick={handleReply}
                    >
                        Send
                    </button>
                </div>
            )}
        </div>
    );
};

const SeperateChatPreview = ({ batchScheduleId, topHeight }: any) => {
    const { scheduleId } = useParams();
    const [mqttCreds, setMqttCreds] = useState(undefined as any);
    const [client, setClient] = useState(undefined as any);
    const [chatType, setChatType] = useState(undefined as any);
    const [connectStatus, setConnectStatus] = useState('Connect' as any);
    const [liveChats, setLiveChats] = useState([] as any[]);
    const ScheduleId = batchScheduleId || scheduleId;
    const [schedule, setSchedule] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        if (!ScheduleId) return;
        const fetchSchedule = async () => {
            try {
                setLoading(true);
                const res = await goApi.get(`/batches/schedules/${ScheduleId}`);
                setSchedule(res.data);
            } catch (err: any) {
                setError(err.message || 'Failed to load schedule');
            } finally {
                setLoading(false);
            }
        };

        fetchSchedule();
    }, [ScheduleId]);
    const isLive = isScheduleLive(schedule);

    const mqttDisconnect = useCallback(() => {
        if (client) {
            try {
                client.end(false, () => {
                    setConnectStatus('DisConnect');
                });
            } catch (error) {
                console.log('disconnect error:', error);
            }
        }
    }, [client]);

    const socketDisconnect = useCallback(() => {
        if (client) {
            try {
                client.disconnect();
                setConnectStatus('DisConnect');
            } catch (error) {
                console.log('disconnect error:', error);
            }
        }
    }, [client]);

    const mqttSub = useCallback(
        (subscription: { topic: any; qos: any }) => {
            if (client) {
                const { topic, qos } = subscription;
                client.subscribe(topic, { qos }, (error: any) => {
                    if (error) {
                        console.log('Subscribe to topics error', error);
                        return;
                    }
                });
            }
        },
        [client],
    );

    const socketSub = useCallback(
        (subscription: { topic: any; qos: any }) => {
            client.subscribe(subscription.topic, (payload: any) => {
                try {
                    if (!payload?.body) return;

                    const reformat = {
                        ...payload.body,
                        userId: payload.body.userId,
                        userName: payload.body.userName,
                        createdAt: payload.body.createdAt,
                    };

                    setLiveChats(prev => {
                        const exist = prev.find(c => c.id === reformat.id);
                        if (exist) return prev;
                        return [reformat, ...prev];
                    });
                } catch (e) {
                    console.error('Error parsing SSE payload', e);
                }
            });
        },
        [client],
    );

    const mqttPublish = (context: { topic: any; qos: any; payload: any }) => {
        if (client) {
            const { topic, qos, payload } = context;
            client.publish(topic, payload, { qos }, (error: any) => {
                if (error) {
                    console.log('Publish error: ', error);
                }
            });
        }
    };

    useEffect(() => {
        setChatType(schedule?.chatMode);

        if (schedule && isScheduleLive(schedule) && schedule?.chatMode !== 'SOCKET') {
            const creds = {
                clientId: 'emqx_react_' + Math.random().toString(16).substring(2, 8),
                username: 'demon',
                password: '111111',
            };
            setMqttCreds(creds);
            setClient(
                mqtt.connect('wss://lcbfc706.ala.us-east-1.emqxsl.com:8084/mqtt', {
                    ...creds,
                }),
            );
            return () => {
                mqttDisconnect();
            };
        }
        if (schedule && isScheduleLive(schedule) && schedule?.chatMode === 'SOCKET') {
            setClient(socketService);
            return () => {
                socketDisconnect();
            };
        }
    }, [ScheduleId]);

    useEffect(() => {
        if (client && schedule) {
            if (schedule?.chatMode === 'SOCKET') socketSub({ topic: `${schedule.id}`, qos: 0 });
            else mqttSub({ topic: `schedule_${schedule.id}`, qos: 0 });
        }
    }, [client]);

    useEffect(() => {
        if (client && schedule?.chatMode !== 'SOCKET') {
            handleMqtt();
        }
    }, [client]);

    function handleMqtt() {
        if (!client) return;

        client.on('connect', () => {
            setConnectStatus('Connected');
        });

        client.on('error', (err: any) => {
            console.error('Connection error: ', err);
            client.end();
        });

        client.on('reconnect', () => {
            setConnectStatus('Reconnecting');
        });

        client.on('message', (topic: any, message: any) => {
            const payload = { topic, message: message.toString() };
            try {
                const { data } = JSON.parse(message.toString());

                const reStructure = {
                    ...data,
                    userId: data.userId,
                    userName: data.userName,
                    createdAt: data.createdAt,
                };

                setLiveChats(prevState => {
                    const exists = prevState.some(
                        chat =>
                            (chat.id && reStructure.id && chat.id === reStructure.id) ||
                            (chat.message === reStructure.message &&
                                chat.userName === reStructure.userName &&
                                Math.abs(
                                    new Date(chat.createdAt).getTime() -
                                        new Date(reStructure.createdAt).getTime(),
                                ) < 500),
                    );

                    if (exists) {
                        return prevState;
                    }

                    return [reStructure, ...prevState];
                });
            } catch (e) {
                console.log('Error parsing MQTT message:', e);
            }
        });
    }

    if (!schedule) {
        return <Skeleton height={500} />;
    }

    return (
        <div
            className={`p-2 flex flex-col gap-2 h-[calc(100vh-${
                topHeight ?? '50px'
            })] overflow-y-hidden`}
            id={'details-section'}
        >
            <div
                className={
                    'w-full h-full p-2 flex gap-2 border relative border-primary-400 rounded-md'
                }
            >
                {loading ? (
                    <div className="p-4 w-full">
                        <Skeleton variant="rectangular" width="100%" height={400} />
                    </div>
                ) : error ? (
                    <> </>
                ) : (
                    <div className={'flex-1'}>
                        <h3 className="text-center text-2xl font-bold uppercase">Chats </h3>
                        <AllChats
                            isLive={isLive}
                            chatType={chatType}
                            scheduleId={ScheduleId}
                            liveChats={liveChats}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
export default SeperateChatPreview;
